import { z } from "zod";
import { BearingDeg, LatLon, SpeedKnots } from "./geometry.js";
import {
  AISInfo,
  EnvironmentState,
  Event,
  VesselState,
  VesselType,
} from "./world.js";

/**
 * Live state of the own ship at a tick.
 */
export const OwnShipState = z.object({
  position: LatLon,
  cog: BearingDeg,
  sog: SpeedKnots,
  heading: BearingDeg,
});
export type OwnShipState = z.infer<typeof OwnShipState>;

/**
 * Live state of a target at a tick. Target state is what the engine produces
 * for rendering — it is not the scenario shape and may contain runtime-only
 * fields like MARPA tracking metadata in future schema versions.
 */
export const TargetState = z.object({
  id: z.string(),
  vesselType: VesselType,
  position: LatLon,
  cog: BearingDeg,
  sog: SpeedKnots,
  rcs: z.number().gte(0).lte(1),
  ais: AISInfo.optional(),
  /** Optional COLREG state — drives day shape and night lights. */
  state: VesselState.optional(),
  /** True if the target is generating a radar return at this tick. */
  visible: z.boolean(),
});
export type TargetState = z.infer<typeof TargetState>;

/**
 * Live state of an active SART (Search and Rescue Transponder) — the
 * battery-powered beacon attached to a person-overboard or life-raft.
 * Real SARTs respond to a radar pulse with a 12-pulse train; on the PPI
 * this paints as 12 dots radiating outward from the casualty position
 * along the bearing from the radar.
 */
export const SartState = z.object({
  id: z.string(),
  position: LatLon,
  /** Sim time the SART was activated (kept on the snapshot so replays
   *  show the correct elapsed-since-activation. */
  activatedAtSimSeconds: z.number().gte(0),
});
export type SartState = z.infer<typeof SartState>;

export const SNAPSHOT_SCHEMA_VERSION = 1 as const;

/**
 * Authoritative state of the world at a single tick. All RadarStateSource
 * implementations produce this; all consumers (PPI engine, MARPA tracker,
 * recording capture) read this. Snapshots are full-state for v1.
 */
export const WorldSnapshot = z.object({
  schemaVersion: z.literal(SNAPSHOT_SCHEMA_VERSION),
  /** Monotonic seconds since scenario start. */
  simTimeSeconds: z.number().gte(0),
  /** ISO datetime; drives tide and daylight in the world. */
  wallClock: z.string().datetime({ offset: true }),
  ownShip: OwnShipState,
  targets: z.array(TargetState),
  /** Active SART transponders. Empty when no MOB has been dropped. */
  sarts: z.array(SartState).default([]),
  environment: EnvironmentState,
  /**
   * Events that fired since the previous snapshot. Used for one-shot UI
   * (alarm tones, MOB alerts) without diffing full state. Empty in steady-state.
   */
  eventsSinceLast: z.array(Event),
});
export type WorldSnapshot = z.infer<typeof WorldSnapshot>;
